import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { LookupItem } from "../models/LookupItem";
import { FacetResult } from "../models/FacetResult";
import { SiteSearchItem } from "../models/SiteSearchItem";
import { SuggestItem } from "../models/SuggestItem";

export interface SiteSearchState {
  keywords: string;
  suggestions: SuggestItem[];
  siteSection: undefined | LookupItem;
  allSiteSections: LookupItem[];
  isLoading: boolean;
  isSuggestLoading: boolean;
  canLoadMore: boolean;
  items: SiteSearchItem[];
  totalCount: number;
  isNoResults: boolean;
  pageSize: number;
  page: number;
  initDone: boolean;
  facetResult: any[];
  recommendations: any[];
}

const initialState: SiteSearchState = {
  keywords: "",
  suggestions: [],
  siteSection: undefined,
  allSiteSections: [],
  isLoading: false,
  isSuggestLoading: false,
  canLoadMore: false,
  items: [],
  totalCount: 0,
  isNoResults: false,
  pageSize: 9,
  page: 1,
  initDone: false,
  facetResult: [],
  recommendations: [],
};

export type SuggestFilter = {
  size?: number;
  searchText?: string;
};

export type SearchFilter = {
  keywords?: string;
  siteSection?: string;
  path?: string;
  page: number;
  pageSize: number;
};

export const siteSearchSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/siteSearchSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/sitesearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const siteSearch = createAsyncThunk<
  FacetResult<SiteSearchItem>,
  SearchFilter
>(
  "data/siteSearch",
  async (payload: SearchFilter): Promise<FacetResult<SiteSearchItem>> => {
    const response = await fetch("/sitesearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<SiteSearchItem>;
  }
);

export const siteSearchSlice = createSlice({
  name: "siteSearch",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords !== action.payload) {
          state.items = [];
          state.recommendations = [];
        state.facetResult = [];
      }

      if (!action.payload) {
        state.totalCount = 0;
        state.canLoadMore = false;
        state.isNoResults = false;
        state.siteSection = undefined;
      }
      state.keywords = action.payload;
      state.page = 1;
    },
    changeSiteSection: (
      state,
      action: PayloadAction<undefined | LookupItem>
    ) => {
      if (state.siteSection?.value !== action.payload?.value) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.siteSection = action.payload;
      state.page = 1;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(siteSearch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        siteSearch.fulfilled,
        (state, action: PayloadAction<FacetResult<SiteSearchItem>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
              state.facetResult = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
              }

              if (action.payload.recommendations.length > 0) {
                  state.recommendations = action.payload.recommendations;
              }

            state.isNoResults = state.items.length === 0;
            state.totalCount = action.payload.totalHits;
            state.facetResult = action.payload.facets;
            state.canLoadMore = state.totalCount > state.items.length;
          }
        }
      )
      .addCase(siteSearch.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(siteSearchSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        siteSearchSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(siteSearchSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changePage,
  changeKeywords,
  changeSiteSection,
  changeInit,
  clearSuggestions,
} = siteSearchSlice.actions;

export default siteSearchSlice.reducer;
